import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, Input } from '@/components/ui';
import { AuthHeading, FormMessage } from './AuthLayout';
import { PasswordField } from './PasswordField';
import { useAuthContext } from './useAuth';
import { cognitoSignIn, cognitoSignUp, friendlyAuthError } from './cognito';
import { isPasswordValid } from '@/lib/password';

export default function SignUpPage() {
  const { refresh } = useAuthContext();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const cleanEmail = email.trim().toLowerCase();
    if (!name.trim()) return setError('Tell us your name.');
    if (!cleanEmail) return setError('Enter your email address.');
    if (!isPasswordValid(password)) return setError('Choose a password that meets all the requirements below.');
    setBusy(true);
    try {
      const result = await cognitoSignUp(name.trim(), cleanEmail, password);
      if (result === 'done') {
        await cognitoSignIn(cleanEmail, password);
        await refresh();
      } else {
        navigate(`/confirm?email=${encodeURIComponent(cleanEmail)}`);
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <AuthHeading title="Create your account" subtitle="Free to start. We will email you a code to confirm your address." />
      <FormMessage error={error} />
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Input label="Name" name="name" autoComplete="name" autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
        <PasswordField
          label="Password"
          name="new-password"
          autoComplete="new-password"
          required
          showStrength
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" size="lg" className="w-full" loading={busy}>
          {busy ? 'Creating account' : 'Create account'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-ink-muted">
        Already have an account?{' '}
        <Link to="/signin" className="font-medium text-link hover:underline">
          Sign in
        </Link>
      </p>
    </>
  );
}
